import { LandingHeader } from './LandingHeader'
import { LandingFooter } from './LandingFooter'

export function PrivacyPage() {
    return (
        <div className="min-h-screen">
            <LandingHeader />
            <main className="pt-20 sm:pt-24 pb-16 px-4 sm:px-6">
                <article className="max-w-3xl mx-auto">
                    <header className="mb-12">
                        <h1 className="text-3xl sm:text-4xl font-bold mb-3">Privacy Policy</h1>
                        <p className="text-sm text-text-muted">
                            Last updated: 9 April 2026
                        </p>
                    </header>

                    <div className="prose-content space-y-10 text-text-secondary leading-relaxed">
                        <Section title="1. Who we are">
                            <p>
                                Endiagram is operated by <strong>Dushyant Suthar</strong>, an individual based in
                                India. The Service is provided free of charge. The{' '}
                                <code className="text-accent">@endiagram/mcp</code> NPM client is published as
                                open source under the MIT licence at{' '}
                                <a
                                    href="https://github.com/dushyant30suthar/endiagram-mcp"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-accent hover:underline"
                                >
                                    github.com/dushyant30suthar/endiagram-mcp
                                </a>; the verification engine, web applications, and infrastructure are
                                proprietary and operator-owned. References to "we", "us", and "our" in this
                                policy mean the operator. The operator is the data controller within the meaning
                                of GDPR Article 4(7) for any personal data processed via this service.
                            </p>
                            <p>
                                Contact: <a href="mailto:privacy@endiagram.com" className="text-accent hover:underline">privacy@endiagram.com</a>
                            </p>
                        </Section>

                        <Section title="2. Scope">
                            <p>
                                This policy covers <code className="text-accent">endiagram.com</code>,{' '}
                                <code className="text-accent">playground.endiagram.com</code>, the public API at{' '}
                                <code className="text-accent">api.endiagram.com</code>, and the{' '}
                                <code className="text-accent">@endiagram/mcp</code> NPM package. It does not cover
                                third-party MCP clients, AI assistants, or editors that you choose to connect to
                                our API independently.
                            </p>
                        </Section>

                        <Section title="3. What we process and why">
                            <p>We log the following metadata for each request to our API:</p>
                            <table className="w-full text-sm border-collapse mt-4">
                                <thead>
                                    <tr className="border-b" style={{ borderColor: 'var(--border)' }}>
                                        <th className="text-left py-2 pr-4 text-text font-semibold">Field</th>
                                        <th className="text-left py-2 pr-4 text-text font-semibold">What it is</th>
                                        <th className="text-left py-2 text-text font-semibold">Why</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <Row
                                        field="cid"
                                        value="HMAC-SHA256(your IP, our salt) truncated to 12 hex characters"
                                        why="Per-IP correlation for abuse detection and request tracing. Not reversible."
                                    />
                                    <Row
                                        field="country"
                                        value="Two-letter country code from the Cloudflare CF-IPCountry header"
                                        why="Geographic distribution for product planning."
                                    />
                                    <Row
                                        field="ua"
                                        value="OS/browser fingerprint, e.g. 'Linux/Chrome' or 'Mac/Safari'"
                                        why="Compatibility metrics. Raw User-Agent is not stored."
                                    />
                                    <Row
                                        field="sess / install"
                                        value="A random UUID generated and stored on your device"
                                        why="Per-installation correlation that survives IP changes. You control deletion."
                                    />
                                    <Row
                                        field="source preview"
                                        value="Up to ~600 characters of the EN source you submitted, sampled occasionally"
                                        why="Diagnosing parse errors and understanding common patterns."
                                    />
                                </tbody>
                            </table>
                            <p className="mt-4 text-sm">
                                <strong>Important:</strong> if your EN source contains secrets, do not submit
                                them. We do not filter content, and source samples may appear in logs as written.
                            </p>
                        </Section>

                        <Section title="4. What we do NOT collect">
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>No advertising trackers</li>
                                <li>No third-party analytics (no Google Analytics, Mixpanel, Segment, PostHog, etc.)</li>
                                <li>No fingerprinting beyond the OS/browser fields listed above</li>
                                <li>No behavioral profiling or cross-site tracking</li>
                                <li>No content of files you have not submitted</li>
                                <li>No environment variables, file paths, or filesystem contents from the NPX tool</li>
                                <li>No raw IP addresses in stored logs (we hash them at request time)</li>
                            </ul>
                        </Section>

                        <Section title="5. Cookies and local storage">
                            <p>
                                We use no HTTP cookies. The playground at{' '}
                                <code className="text-accent">playground.endiagram.com</code> uses your browser's{' '}
                                <code className="text-accent">localStorage</code> to remember your editor state
                                (so reloads don't lose your work) and a randomly generated install ID. You can
                                clear this data at any time using the "Clear local data" button in the playground
                                footer, or via your browser's site settings.
                            </p>
                            <p>
                                The <code className="text-accent">@endiagram/mcp</code> NPM client stores its
                                install ID in a file at <code className="text-accent">~/.endiagram/install-id</code>{' '}
                                with file mode 0600. You can delete it at any time, or set the environment
                                variable <code className="text-accent">ENDIAGRAM_TELEMETRY=off</code> to disable
                                it entirely.
                            </p>
                        </Section>

                        <Section title="6. Lawful basis (GDPR Article 6)">
                            <p>
                                For server-side request metadata (cid, country, UA, source samples) we rely on
                                <strong> legitimate interest</strong> under Article 6(1)(f) — operating, securing,
                                and improving a free developer tool. The processing is minimal, the data is
                                pseudonymized, and the impact on data subjects is low.
                            </p>
                            <p>
                                For browser localStorage and the NPM install-id file we rely on{' '}
                                <strong>necessity for performance of the service requested</strong> under
                                Article 6(1)(b). These identifiers are part of the editor-state persistence
                                feature that lets you reload the page without losing work, and the consistent
                                request correlation that lets us debug issues per installation.
                            </p>
                            <p>
                                The browser localStorage usage falls under the "strictly necessary" exemption of
                                ePrivacy Directive Article 5(3) because it is essential to provide the playground
                                feature you explicitly requested by visiting the page.
                            </p>
                        </Section>

                        <Section title="7. Third parties">
                            <p>We use the following service providers (sub-processors):</p>
                            <ul className="list-disc list-inside space-y-2 ml-2">
                                <li>
                                    <strong>Cloudflare, Inc.</strong> (US/global) — CDN, DNS, TLS termination, bot
                                    protection. Cloudflare sees raw IPs.{' '}
                                    <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                                        Cloudflare privacy policy
                                    </a>
                                </li>
                                <li>
                                    <strong>Railway Corp</strong> (US) — application hosting for our API. Railway
                                    sees raw IPs at the network layer until our application hashes them.{' '}
                                    <a href="https://railway.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                                        Railway privacy policy
                                    </a>
                                </li>
                                <li>
                                    <strong>npm, Inc. / GitHub</strong> (US) — hosts the{' '}
                                    <code className="text-accent">@endiagram/mcp</code> package. They see download
                                    metrics from your IP when you install or run the package.{' '}
                                    <a href="https://docs.npmjs.com/policies/privacy" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                                        npm privacy policy
                                    </a>
                                </li>
                            </ul>
                            <p className="mt-3 text-sm">
                                We self-host all fonts and assets. We do not load any resources from
                                Google Fonts, jsDelivr, or any other third-party CDN.
                            </p>
                        </Section>

                        <Section title="8. International transfers">
                            <p>
                                The data controller is in India. Our infrastructure providers (Cloudflare,
                                Railway, npm) are based in the United States with global edge presence. When you
                                use this service from the EU, your data is transferred outside the EEA. The lawful
                                basis for this transfer under GDPR Article 49(1)(b) is that the transfer is
                                necessary for the performance of the service you have voluntarily requested.
                            </p>
                        </Section>

                        <Section title="9. Your rights">
                            <p>If you are in the EU, UK, or another jurisdiction with similar protections, you have the right to:</p>
                            <ul className="list-disc list-inside space-y-2 ml-2 mt-2">
                                <li>
                                    <strong>Access</strong> — request a copy of the log lines associated with your
                                    identifier. Provide your install ID (from{' '}
                                    <code className="text-accent">~/.endiagram/install-id</code> or your browser's
                                    localStorage <code className="text-accent">en-install-id</code> key) and we
                                    will return matching lines from our logs.
                                </li>
                                <li>
                                    <strong>Erasure</strong> — the fastest erasure path is self-service: clear
                                    your localStorage or delete{' '}
                                    <code className="text-accent">~/.endiagram/install-id</code>. This severs
                                    future correlation immediately. For existing log lines, see the retention
                                    section below.
                                </li>
                                <li><strong>Rectification</strong> — log entries are immutable historical records; correction is generally not applicable</li>
                                <li><strong>Restriction</strong> — stop using the service or set <code className="text-accent">ENDIAGRAM_TELEMETRY=off</code></li>
                                <li><strong>Portability</strong> — log lines are plain text and we can email them to you on request</li>
                                <li><strong>Objection</strong> — same as restriction; you can stop using the service at any time</li>
                            </ul>
                            <p className="mt-3 text-sm">
                                To exercise any of these rights, email{' '}
                                <a href="mailto:privacy@endiagram.com" className="text-accent hover:underline">privacy@endiagram.com</a>.
                                We aim to respond within 30 days as required by GDPR Article 12(3).
                            </p>
                        </Section>

                        <Section title="10. Retention">
                            <p>
                                Server logs are retained for approximately <strong>30 days</strong> in rotating
                                files and then permanently deleted. Aggregated metrics derived from logs (e.g.
                                anonymous request counts) may be kept longer in non-identifiable form.
                            </p>
                            <p>
                                <strong>Honest note about erasure:</strong> purging individual lines from a
                                rotating tail-of-log infrastructure requires disproportionate effort for
                                pseudonymous data. In practice, your data is fully erased within 30 days by the
                                normal rotation cycle. You can accelerate the loss of correlation immediately by
                                clearing your install/session ID on your device. We acknowledge this limit
                                upfront so you are not surprised by it; GDPR Recital 66 permits this approach
                                where the effort would be disproportionate.
                            </p>
                        </Section>

                        <Section title="11. Children">
                            <p>
                                Endiagram is a developer tool not directed at children. We do not knowingly
                                collect personal data from anyone under 16. If you become aware that a child has
                                used the service and provided personal data, contact us and we will delete it.
                            </p>
                        </Section>

                        <Section title="12. Changes to this policy">
                            <p>
                                When we update this policy we will change the "Last updated" date at the top of
                                this page. We maintain version history of this policy in our internal source
                                control; if you need a specific historical revision for compliance or audit
                                purposes, contact us at{' '}
                                <a href="mailto:privacy@endiagram.com" className="text-accent hover:underline">privacy@endiagram.com</a>{' '}
                                and we will provide it.
                            </p>
                        </Section>

                        <Section title="13. Contact">
                            <p>
                                For any privacy-related question or to exercise the rights described above:
                            </p>
                            <p>
                                <a href="mailto:privacy@endiagram.com" className="text-accent hover:underline">privacy@endiagram.com</a>
                            </p>
                            <p className="text-sm">
                                We aim to respond within 30 days, in line with GDPR Article 12(3).
                            </p>
                        </Section>

                        <Section title="14. Supervisory authority">
                            <p>
                                If you are in the EU and you believe we have not handled your personal data
                                lawfully, you have the right to lodge a complaint with your national data
                                protection authority. A directory is available at the{' '}
                                <a
                                    href="https://edpb.europa.eu/about-edpb/about-edpb/members_en"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-accent hover:underline"
                                >
                                    European Data Protection Board
                                </a>.
                            </p>
                            <p className="text-sm">
                                Note for California residents: the disclosures above satisfy the CCPA's
                                "notice at collection" requirements. We do not sell or share personal information.
                            </p>
                        </Section>
                    </div>
                </article>
            </main>
            <LandingFooter />
        </div>
    )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section>
            <h2 className="text-xl font-semibold text-text mb-3">{title}</h2>
            <div className="space-y-3">{children}</div>
        </section>
    )
}

function Row({ field, value, why }: { field: string; value: string; why: string }) {
    return (
        <tr className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
            <td className="py-3 pr-4 align-top">
                <code className="text-accent font-mono text-xs whitespace-nowrap">{field}</code>
            </td>
            <td className="py-3 pr-4 align-top text-sm">{value}</td>
            <td className="py-3 align-top text-sm">{why}</td>
        </tr>
    )
}
